import {
  Refresh as RefreshIcon,
  Settings as SettingsIcon,
} from "@mui/icons-material";
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  IconButton,
  Tooltip,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControlLabel,
  Switch,
  Stack,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { BaseWidgetProps } from "../../types/dashboard";
import { widgetDataManager } from "./WidgetDataManager";
import { widgetLogger } from "./WidgetLogger";

export const Widget: React.FC<BaseWidgetProps> = ({
  config,
  loading: initialLoading,
  error: initialError,
  onSettingsChange,
  onRefresh,
  children,
}) => {
  const { t } = useTranslation(['widgets', 'common']);
  const [loading, setLoading] = useState(initialLoading);
  const [error, setError] = useState<Error | undefined>(initialError);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settingsForm, setSettingsForm] = useState({
    refreshIntervalSeconds: Math.max(5, Math.round((config.refreshInterval ?? 300000) / 1000)),
    autoRefresh: Boolean(config.settings?.['autoRefresh'] ?? false),
  });

  const autoRefreshSetting = config.settings?.['autoRefresh'];

  useEffect(() => {
    widgetLogger.info(config.id, "Виджет инициализирован", { config });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.id]);

  useEffect(() => {
    setSettingsForm({
      refreshIntervalSeconds: Math.max(5, Math.round((config.refreshInterval ?? 300000) / 1000)),
      autoRefresh: Boolean(autoRefreshSetting ?? false),
    });
  }, [config.refreshInterval, autoRefreshSetting]);

  useEffect(() => {
    const shouldAutoRefresh = Boolean(config.settings?.['autoRefresh'] ?? false);
    if (!shouldAutoRefresh || !config.refreshInterval || !onRefresh) {
      return;
    }

    const interval = setInterval(() => {
      handleRefresh();
    }, Math.max(1000, config.refreshInterval));

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.id, config.refreshInterval, config.settings?.['autoRefresh'], onRefresh]);

  const handleRefresh = async () => {
    try {
      setLoading(true);
      setError(undefined);
      widgetLogger.debug(config.id, "Начало обновления виджета");

      // Очищаем кэш для этого виджета
      widgetDataManager.clearCache(config.id);

      // Вызываем пользовательский обработчик обновления
      await onRefresh?.();

      widgetLogger.info(config.id, "Виджет успешно обновлен");
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      setError(error);
      widgetLogger.error(config.id, "Ошибка при обновлении виджета", { error });
    } finally {
      setLoading(false);
    }
  };

  const handleSettings = () => {
    widgetLogger.debug(config.id, "Открытие настроек виджета");
    setSettingsOpen(true);
  };

  const handleSettingsClose = () => {
    setSettingsOpen(false);
  };

  const handleSettingsSave = () => {
    const refreshIntervalMs = Math.max(5, settingsForm.refreshIntervalSeconds) * 1000;
    onSettingsChange?.({
      refreshInterval: refreshIntervalMs,
      autoRefresh: settingsForm.autoRefresh,
    });
    widgetLogger.info(config.id, t('settings.saved', { defaultValue: 'Настройки виджета сохранены' }), {
      refreshInterval: refreshIntervalMs,
      autoRefresh: settingsForm.autoRefresh,
    });
    setSettingsOpen(false);
  };

  return (
    <Card
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        position: "relative",
        overflow: "hidden",
        transition: "box-shadow 0.2s ease-in-out",
        "&:hover": {
          boxShadow: (theme) => theme.shadows[4],
        },
      }}
    >
      <CardHeader
        title={config.title}
        subheader={config.description}
        sx={{
          pb: 1,
          "& .MuiCardHeader-title": {
            fontSize: "1.1rem",
            fontWeight: 600,
          },
          "& .MuiCardHeader-subheader": {
            fontSize: "0.875rem",
          },
        }}
        action={
          <Box>
            {onRefresh && (
              <Tooltip title="Обновить">
                <IconButton
                  onClick={handleRefresh}
                  size="small"
                  disabled={loading ?? false}
                >
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
            )}
            {onSettingsChange && (
              <Tooltip title="Настройки">
                <span>
                  <IconButton onClick={handleSettings} size="small">
                    <SettingsIcon />
                  </IconButton>
                </span>
              </Tooltip>
            )}
          </Box>
        }
      />
      <CardContent sx={{ flexGrow: 1, position: "relative" }}>
        {loading && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "rgba(255, 255, 255, 0.8)",
              zIndex: 1,
              backdropFilter: "blur(2px)",
            }}
          >
            <CircularProgress size={24} />
          </Box>
        )}
        {error && (
          <Box
            sx={{
              p: 2,
              color: "error.main",
              textAlign: "center",
              backgroundColor: "error.light",
              borderRadius: 1,
              border: "1px solid",
              borderColor: "error.main",
            }}
          >
            <Typography variant="body2" fontWeight="bold">
              Ошибка загрузки
            </Typography>
            <Typography variant="caption">{error.message}</Typography>
          </Box>
        )}
        {!loading && !error && children}
      </CardContent>
      {onSettingsChange && (
        <Dialog open={settingsOpen} onClose={handleSettingsClose} maxWidth="sm" fullWidth>
          <DialogTitle>{t('settings.title', { defaultValue: 'Настройки виджета' })}</DialogTitle>
          <DialogContent>
            <Stack spacing={3} sx={{ mt: 1 }}>
              <TextField
                label={t('settings.refreshInterval', { defaultValue: 'Интервал автообновления (сек)' })}
                type="number"
                value={settingsForm.refreshIntervalSeconds}
                onChange={(event) => {
                  const next = Number(event.target.value);
                  setSettingsForm((prev) => ({
                    ...prev,
                    refreshIntervalSeconds: Number.isFinite(next) ? Math.max(5, next) : prev.refreshIntervalSeconds,
                  }));
                }}
                inputProps={{ min: 5, max: 3600, step: 5 }}
                helperText={t('settings.refreshIntervalHelp', { defaultValue: 'Минимум 5 секунд, максимум 3600 секунд' })}
                fullWidth
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={settingsForm.autoRefresh}
                    onChange={(event) =>
                      setSettingsForm((prev) => ({
                        ...prev,
                        autoRefresh: event.target.checked,
                      }))
                    }
                  />
                }
                label={t('settings.autoRefresh', { defaultValue: 'Автообновление' })}
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleSettingsClose} color="inherit">
              {t('common:cancel')}
            </Button>
            <Button onClick={handleSettingsSave} variant="contained">
              {t('common:actions.save')}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Card>
  );
};
